import React from 'react';
import { Box, Typography, Avatar, Button, IconButton } from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Share as ShareIcon,
  Call as CallIcon,
  QrCode2 as QrCodeIcon,
  ContentCopy as CopyIcon,
  LocationOn as LocationIcon,
  AccessTime as ClockIcon,
  Print as FaxIcon,
  WhatsApp as WhatsAppIcon,
  Map as MapIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { getConfig } from './CardRenderer';
import { sortCardSections } from '../utils/sortCardSections';
import { normalizeGalleryLayout } from '../utils/galleryLayout';
import BannerStrip from './BannerStrip';
import GalleryImageMosaic from './GalleryImageMosaic';
import { TitleCompanyStack } from './TitleCompanyStack';
import { AboutFormattedText } from './AboutFormattedText';

const getSocialIcon = (platform, size = 16) => {
  const s = (platform || '').toLowerCase();
  if (s === 'linkedin') return <LinkedInIcon sx={{ fontSize: size }} />;
  if (s === 'twitter' || s === 'x') return <TwitterIcon sx={{ fontSize: size }} />;
  if (s === 'instagram') return <InstagramIcon sx={{ fontSize: size }} />;
  if (s === 'facebook') return <FacebookIcon sx={{ fontSize: size }} />;
  return <ShareIcon sx={{ fontSize: size }} />;
};

const getSocialColor = (platform) => {
  const s = (platform || '').toLowerCase();
  if (s === 'linkedin') return '#0077b5';
  if (s === 'twitter' || s === 'x') return '#1da1f2';
  if (s === 'instagram') return '#e4405f';
  if (s === 'facebook') return '#1877f2';
  return '#6b7280';
};

const initials = (name) =>
  (name || 'YN').split(/\s+/).map(s => s[0]).join('').toUpperCase().slice(0, 2);

const buildFullAddress = (cardData) => {
  return [cardData?.address, cardData?.city, cardData?.state, cardData?.country]
    .filter(Boolean).join(', ');
};

// ─── Shared section renderers ───

const SectionAbout = ({ cardData, textColor = '#475569', titleColor = '#1e293b', align = 'center' }) => {
  if (!cardData?.about) return null;
  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: titleColor, mb: 0.5, textAlign: align }}>
        About
      </Typography>
      <AboutFormattedText
        text={cardData.about}
        variant="inherit"
        component="div"
        textAlign={align}
        sx={{ fontSize: '0.6rem', color: textColor, lineHeight: 1.5 }}
      />
    </Box>
  );
};

const SectionContact = ({ cardData, accent = '#00A7E3', titleColor = '#1e293b', textColor = '#475569', align = 'center', variant = 'default' }) => {
  const hasBasicContact = cardData?.email || cardData?.phone || cardData?.website;
  const fullAddress = buildFullAddress(cardData);
  const hasExtended = fullAddress || cardData?.additionalPhone || cardData?.fax || cardData?.officeHours;
  if (!hasBasicContact && !hasExtended) return null;

  const iconSize = variant === 'compact' ? 10 : 12;

  const contactRows = [
    cardData?.additionalPhone && { icon: <PhoneIcon sx={{ fontSize: iconSize, color: accent }} />, text: cardData.additionalPhone },
    cardData?.email && { icon: <EmailIcon sx={{ fontSize: iconSize, color: accent }} />, text: cardData.email },
    cardData?.website && { icon: <WebIcon sx={{ fontSize: iconSize, color: accent }} />, text: cardData.website },
    fullAddress && { icon: <LocationIcon sx={{ fontSize: iconSize, color: accent }} />, text: fullAddress },
    cardData?.fax && { icon: <FaxIcon sx={{ fontSize: iconSize, color: accent }} />, text: cardData.fax },
    cardData?.officeHours && { icon: <ClockIcon sx={{ fontSize: iconSize, color: accent }} />, text: cardData.officeHours },
  ].filter(Boolean);

  if (!contactRows.length) return null;

  const fontSize = variant === 'compact' ? '0.55rem' : '0.6rem';
  const gap = variant === 'compact' ? 0.5 : 0.75;
  const iconBoxSize = variant === 'compact' ? 18 : 22;

  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: titleColor, mb: 0.75, textAlign: align }}>
        Contact
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap }}>
        {contactRows.map((row, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: iconBoxSize, height: iconBoxSize, borderRadius: '50%', bgcolor: `${accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {row.icon}
            </Box>
            <Typography sx={{ fontSize, color: textColor, wordBreak: 'break-word' }}>{row.text}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const SectionCustom = ({ sections, cardData, titleColor = '#1e293b', textColor = '#475569', align = 'center' }) => {
  const customs = sections.filter(s => s.id.startsWith('custom-') && s.enabled);
  if (!customs.length) return null;
  return customs.map(section => (
    <Box key={section.id} sx={{ px: 1.5, pb: 1.5 }}>
      {cardData?.[`${section.id}_header`] && (
        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: titleColor, mb: 0.5, textAlign: align }}>
          {cardData[`${section.id}_header`]}
        </Typography>
      )}
      {cardData?.[`${section.id}_description`] && (
        <Typography sx={{ fontSize: '0.6rem', color: textColor, textAlign: align, lineHeight: 1.5 }}>
          {cardData[`${section.id}_description`]}
        </Typography>
      )}
    </Box>
  ));
};

const SectionLinks = ({ sections, cardData, accent = '#00A7E3', borderColor = '#e2e8f0', titleColor = '#1e293b' }) => {
  const linksSections = sections.filter(s => s.id.startsWith('links-') && s.enabled);
  if (!linksSections.length) return null;
  return linksSections.map(section => {
    const links = cardData?.[`${section.id}_links`]?.filter(l => l.title && l.url);
    if (!links?.length) return null;
    return (
      <Box key={section.id} sx={{ px: 1.5, pb: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: titleColor, mb: 0.75, textAlign: 'center' }}>
          Useful Links
        </Typography>
        {links.map((link, idx) => (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', p: 1, mb: 0.5, border: `1px solid ${borderColor}`, borderRadius: '6px' }}>
            <WebIcon sx={{ fontSize: 12, color: accent, mr: 0.75 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: titleColor }} noWrap>{link.title}</Typography>
              <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8' }} noWrap>{link.url}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  });
};

const SectionImages = ({ sections, cardData, titleColor = '#1e293b' }) => {
  const imageSections = sections.filter(s => s.id.startsWith('images-') && s.enabled);
  if (!imageSections.length) return null;
  return imageSections.map(section => {
    const images = cardData?.[`${section.id}_images`]?.filter(img => img.url);
    if (!images?.length) return null;
    const layout = cardData?.[`${section.id}_galleryLayout`];
    return (
      <Box key={section.id} sx={{ px: 1.5, pb: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: titleColor, mb: 0.75, textAlign: 'center' }}>
          Gallery
        </Typography>
        <GalleryImageMosaic
          images={images}
          layout={layout}
          imgMinHeight={70}
          gap={0.5}
          captionFontSize="0.45rem"
        />
      </Box>
    );
  });
};

const SectionAppointment = ({ cardData, accent }) => {
  if (!cardData?.calendarUrl) return null;
  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Button fullWidth size="small" variant="contained"
        sx={{ borderRadius: '8px', py: 0.75, fontSize: '0.65rem', fontWeight: 600, textTransform: 'none', bgcolor: accent, boxShadow: 'none' }}>
        {cardData.appointmentText || 'Book Appointment'}
      </Button>
    </Box>
  );
};

const SectionWhatsApp = ({ cardData }) => {
  if (!cardData?.whatsapp) return null;
  return (
    <Box sx={{ px: 1.5, pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 0.75, borderRadius: '8px', bgcolor: '#dcfce7' }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 0.75, flexShrink: 0 }}>
          <WhatsAppIcon sx={{ color: 'white', fontSize: 12 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#166534' }}>Chat on WhatsApp</Typography>
          <Typography sx={{ fontSize: '0.48rem', color: '#15803d' }}>{cardData.whatsapp}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const SectionGoogleMaps = ({ cardData }) => {
  if (!cardData?.googleMapsUrl) return null;
  return (
    <Box sx={{ px: 1.5, pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 0.75, borderRadius: '8px', bgcolor: '#eff6ff' }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#4285F4', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 0.75, flexShrink: 0 }}>
          <MapIcon sx={{ color: 'white', fontSize: 12 }} />
        </Box>
        <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#1e40af' }}>View on Google Maps</Typography>
      </Box>
    </Box>
  );
};

const PAYMENT_META = [
  { key: 'gpay', label: 'GPay', color: '#4285F4' },
  { key: 'phonepe', label: 'PhonePe', color: '#5f259f' },
  { key: 'paytm', label: 'Paytm', color: '#00BAF2' },
  { key: 'upiId', label: 'UPI', color: '#4CAF50' },
];

const SectionPayments = ({ cardData, titleColor = '#1e293b' }) => {
  const active = PAYMENT_META.filter(m => cardData?.[m.key]);
  if (!active.length) return null;
  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: titleColor, mb: 0.5, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <PaymentIcon sx={{ fontSize: 14 }} /> Pay Me
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
        {active.map(m => (
          <Box key={m.key} sx={{ px: 0.75, py: 0.3, borderRadius: '6px', bgcolor: m.color + '15', display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: m.color }} />
            <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: m.color }}>{m.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const FloatingButtons = () => (
  <Box sx={{ position: 'absolute', bottom: 12, left: 12, display: 'flex', flexDirection: 'column', gap: 0.75, zIndex: 5 }}>
    {[QrCodeIcon, CopyIcon].map((Icon, i) => (
      <Box key={i} sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
        <Icon sx={{ fontSize: 14, color: 'white' }} />
      </Box>
    ))}
  </Box>
);

const PoweredBy = ({ color = '#94a3b8' }) => (
  <Typography sx={{ fontSize: '0.5rem', color, textAlign: 'center', py: 1 }}>
    Powered by WebCard Digital
  </Typography>
);

/** Renders section blocks in saved order (skips basic-info). */
function flatOrderedSectionNodes(sections, cardData, socialLinks, accent, layout, opts = {}) {
  const {
    customLinks = [],
    customSections = [],
    gallerySection = { layout: 'grid6', images: [] },
  } = opts;
  const enabledSocials = socialLinks.filter((l) => l.enabled && l.url);
  const enabledCustomLinks = (customLinks || []).filter((l) => l.enabled && l.title && l.url);
  const alignBlocks = layout === 'rows' || layout === 'split' ? 'left' : 'center';
  return sortCardSections(sections).flatMap((section) => {
    if (!section.enabled || section.id === 'basic-info') return [];
    switch (section.id) {
      case 'about':
      case 'about-section':
        if (!cardData?.about) return [];
        if (layout === 'split')
          return [
            <Box key={section.id} sx={{ mt: 1.25 }}>
              <AboutFormattedText
                text={cardData.about}
                variant="inherit"
                component="div"
                sx={{ fontSize: '0.6rem', color: '#475569', lineHeight: 1.5 }}
              />
            </Box>,
          ];
        if (layout === 'hero' || layout === 'rows') return [<SectionAbout key={section.id} cardData={cardData} align={layout === 'rows' ? 'left' : 'center'} />];
        return [];
      case 'contact':
      case 'contact-info':
        if (layout === 'split')
          return [<SectionContact key={section.id} cardData={cardData} accent={accent} align="left" variant="compact" />];
        if (layout === 'hero' || layout === 'rows')
          return [<SectionContact key={section.id} cardData={cardData} accent={accent} align={layout === 'rows' ? 'left' : 'center'} />];
        return [];
      case 'social':
      case 'social-links':
        if (!enabledSocials.length) return [];
        if (layout === 'split')
          return [
            <Box key={section.id} sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
              {enabledSocials.map((link) => (
                <Box key={link.id} sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: getSocialColor(link.platform), display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                  {getSocialIcon(link.platform, 13)}
                </Box>
              ))}
            </Box>,
          ];
        if (layout === 'hero')
          return [
            <Box key={section.id} sx={{ px: 1.5, pb: 1.5, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              {enabledSocials.map((link) => (
                <Box key={link.id} sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: getSocialColor(link.platform), display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                  {getSocialIcon(link.platform, 14)}
                </Box>
              ))}
            </Box>,
          ];
        if (layout === 'rows')
          return [
            <Box key={section.id} sx={{ px: 1.5, pb: 1.5 }}>
              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, mb: 0.75 }}>Connect</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {enabledSocials.map((link) => (
                  <IconButton key={link.id} size="small" sx={{ bgcolor: getSocialColor(link.platform) + '15', color: getSocialColor(link.platform), width: 28, height: 28 }}>
                    {getSocialIcon(link.platform, 14)}
                  </IconButton>
                ))}
              </Box>
            </Box>,
          ];
        return [];
      case 'appointment':
        if (layout === 'split' && cardData?.calendarUrl)
          return [
            <Box key={section.id} sx={{ mt: 1.25 }}>
              <Button fullWidth size="small" variant="outlined" component="a" href={cardData.calendarUrl} target="_blank" rel="noopener noreferrer"
                sx={{ borderRadius: '6px', fontWeight: 600, textTransform: 'none', py: 0.6, fontSize: '0.65rem', borderColor: accent, color: accent }}>
                {cardData.appointmentText || 'Book Appointment'}
              </Button>
            </Box>,
          ];
        if (layout === 'split' && !cardData?.calendarUrl) return [];
        return [<SectionAppointment key={section.id} cardData={cardData} accent={accent} />];
      case 'custom-sections': {
        const blocks = (customSections || []).filter((c) => c.enabled && (c.title || c.description));
        if (!blocks.length) return [];
        return blocks.map((c, i) => (
          <Box key={`csec-${i}`} sx={{ px: 1.5, pb: 1.5 }}>
            {c.title ? (
              <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#1e293b', mb: 0.5, textAlign: alignBlocks }}>{c.title}</Typography>
            ) : null}
            {c.description ? (
              <Typography sx={{ fontSize: '0.6rem', color: '#475569', textAlign: alignBlocks, lineHeight: 1.5 }}>{c.description}</Typography>
            ) : null}
          </Box>
        ));
      }
      case 'custom-links':
        if (!enabledCustomLinks.length) return [];
        return [
          <Box key="custom-links" sx={{ px: 1.5, pb: 1.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#1e293b', mb: 0.75, textAlign: 'center' }}>Useful Links</Typography>
            {enabledCustomLinks.map((link, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', p: 1, mb: 0.5, border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <WebIcon sx={{ fontSize: 12, color: accent, mr: 0.75 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#1e293b' }} noWrap>{link.title}</Typography>
                  <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8' }} noWrap>{link.url}</Typography>
                </Box>
              </Box>
            ))}
          </Box>,
        ];
      case 'gallery': {
        const images = (gallerySection?.images || []).filter((img) => img.url);
        if (!images.length) return [];
        const gl = normalizeGalleryLayout(gallerySection?.layout);
        return [
          <Box key="gallery" sx={{ px: 1.5, pb: 1.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#1e293b', mb: 0.75, textAlign: 'center' }}>Gallery</Typography>
            <GalleryImageMosaic images={images} layout={gl} imgMinHeight={70} gap={0.5} captionFontSize="0.45rem" />
          </Box>,
        ];
      }
      default:
        if (section.id.startsWith('custom-'))
          return [<SectionCustom key={section.id} sections={[section]} cardData={cardData} align={alignBlocks} />];
        if (section.id.startsWith('links-')) return [<SectionLinks key={section.id} sections={[section]} cardData={cardData} accent={accent} />];
        if (section.id.startsWith('images-')) return [<SectionImages key={section.id} sections={[section]} cardData={cardData} />];
        return [];
    }
  });
}

function flatDarkOrderedSectionNodes(sections, cardData, socialLinks, accent, surfaceBg, textPrimary, textSecondary, textMuted, opts = {}) {
  const {
    customLinks = [],
    customSections = [],
    gallerySection = { layout: 'grid6', images: [] },
  } = opts;
  const enabledSocials = socialLinks.filter((l) => l.enabled && l.url);
  const enabledCustomLinks = (customLinks || []).filter((l) => l.enabled && l.title && l.url);
  return sortCardSections(sections).flatMap((section) => {
    if (!section.enabled || section.id === 'basic-info') return [];
    switch (section.id) {
      case 'about':
      case 'about-section':
        if (!cardData?.about) return [];
        return [
          <Box key={section.id} sx={{ bgcolor: surfaceBg, borderRadius: '8px', p: 1.25 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', color: accent, mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>About</Typography>
            <AboutFormattedText
              text={cardData.about}
              variant="inherit"
              component="div"
              sx={{ fontSize: '0.58rem', color: textSecondary, lineHeight: 1.6 }}
              markSx={{ backgroundColor: 'rgba(250, 204, 21, 0.28)' }}
            />
          </Box>,
        ];
      case 'contact':
      case 'contact-info': {
        const fullAddress = buildFullAddress(cardData);
        const rows = [
          cardData?.phone && { icon: <PhoneIcon sx={{ fontSize: 10, color: accent }} />, text: cardData.phone },
          cardData?.additionalPhone && { icon: <PhoneIcon sx={{ fontSize: 10, color: accent }} />, text: cardData.additionalPhone },
          cardData?.email && { icon: <EmailIcon sx={{ fontSize: 10, color: accent }} />, text: cardData.email },
          cardData?.website && { icon: <WebIcon sx={{ fontSize: 10, color: accent }} />, text: cardData.website },
          fullAddress && { icon: <LocationIcon sx={{ fontSize: 10, color: accent }} />, text: fullAddress },
          cardData?.fax && { icon: <FaxIcon sx={{ fontSize: 10, color: accent }} />, text: cardData.fax },
          cardData?.officeHours && { icon: <ClockIcon sx={{ fontSize: 10, color: accent }} />, text: cardData.officeHours },
        ].filter(Boolean);
        if (!rows.length) return [];
        return [
          <Box key={section.id} sx={{ bgcolor: surfaceBg, borderRadius: '8px', p: 1.25 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', color: accent, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contact</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {rows.map((row, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: `${accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{row.icon}</Box>
                  <Typography sx={{ fontSize: '0.55rem', color: textSecondary, wordBreak: 'break-word' }}>{row.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>,
        ];
      }
      case 'social':
      case 'social-links':
        if (!enabledSocials.length) return [];
        return [
          <Box key={section.id} sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, py: 0.5, px: 0.5 }}>
            {enabledSocials.map((link) => (
              <Box key={link.id} sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: getSocialColor(link.platform), display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${getSocialColor(link.platform)}40` }}>
                {getSocialIcon(link.platform, 13)}
              </Box>
            ))}
          </Box>,
        ];
      case 'appointment':
        return [<SectionAppointment key={section.id} cardData={cardData} accent={accent} />];
      case 'custom-sections': {
        const blocks = (customSections || []).filter((c) => c.enabled && (c.title || c.description));
        if (!blocks.length) return [];
        return blocks.map((c, i) => (
          <Box key={`dcsec-${i}`} sx={{ bgcolor: surfaceBg, borderRadius: '8px', p: 1.25 }}>
            {c.title ? (
              <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', color: accent, mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.title}</Typography>
            ) : null}
            {c.description ? (
              <Typography sx={{ fontSize: '0.58rem', color: textSecondary, lineHeight: 1.6 }}>{c.description}</Typography>
            ) : null}
          </Box>
        ));
      }
      case 'custom-links':
        if (!enabledCustomLinks.length) return [];
        return [
          <Box key="d-custom-links" sx={{ bgcolor: surfaceBg, borderRadius: '8px', p: 1.25 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', color: accent, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Useful Links</Typography>
            {enabledCustomLinks.map((link, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', p: 1, mb: 0.5, border: `1px solid ${textMuted}44`, borderRadius: 1 }}>
                <WebIcon sx={{ fontSize: 12, color: accent, mr: 0.75 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: textPrimary }} noWrap>{link.title}</Typography>
                  <Typography sx={{ fontSize: '0.5rem', color: textSecondary }} noWrap>{link.url}</Typography>
                </Box>
              </Box>
            ))}
          </Box>,
        ];
      case 'gallery': {
        const images = (gallerySection?.images || []).filter((img) => img.url);
        if (!images.length) return [];
        const gl = normalizeGalleryLayout(gallerySection?.layout);
        return [
          <Box key="d-gallery">
            <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', color: accent, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Gallery</Typography>
            <GalleryImageMosaic images={images} layout={gl} imgMinHeight={70} gap={0.5} captionFontSize="0.45rem" />
          </Box>,
        ];
      }
      default:
        if (section.id.startsWith('custom-')) {
          if (!(cardData?.[`${section.id}_header`] || cardData?.[`${section.id}_description`])) return [];
          return [
            <Box key={section.id} sx={{ bgcolor: surfaceBg, borderRadius: '8px', p: 1.25 }}>
              {cardData[`${section.id}_header`] && (
                <Typography sx={{ fontWeight: 600, fontSize: '0.65rem', color: accent, mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{cardData[`${section.id}_header`]}</Typography>
              )}
              {cardData[`${section.id}_description`] && (
                <Typography sx={{ fontSize: '0.58rem', color: textSecondary, lineHeight: 1.6 }}>{cardData[`${section.id}_description`]}</Typography>
              )}
            </Box>,
          ];
        }
        if (section.id.startsWith('links-')) return [<SectionLinks key={section.id} sections={[section]} cardData={cardData} accent={accent} borderColor={`${textMuted}44`} titleColor={textPrimary} />];
        if (section.id.startsWith('images-')) return [<SectionImages key={section.id} sections={[section]} cardData={cardData} titleColor={textPrimary} />];
        return [];
    }
  });
}

// ═══════════════════════════════════════════════════════════════
// HERO LAYOUT — gradient header, centered avatar, pill buttons
// ═══════════════════════════════════════════════════════════════
const HeroLayout = ({ cardData, cfg, sections, socialLinks, customLinks, customSections, gallerySection }) => {
  const accent = cfg.accent || '#00A7E3';
  const name = cardData?.name || 'Your Name';
  const title = cardData?.title || '';
  const company = cardData?.company || '';
  const ini = initials(name);
  const previewOpts = { customLinks, customSections, gallerySection };

  return (
    <Box sx={{ bgcolor: 'white', minHeight: '100%' }}>
      {cardData?.bannerImageUrl && <BannerStrip imageUrl={cardData.bannerImageUrl} />}

      <Box sx={{ background: cfg.headerBg, p: 2.5, pb: 2, textAlign: 'center', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -25, right: -25, width: 90, height: 90, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Avatar src={cardData?.profileImageUrl || undefined}
          sx={{ width: 72, height: 72, mx: 'auto', mb: 1, border: '3px solid rgba(255,255,255,0.4)', bgcolor: cfg.avatarBg || accent, color: 'white', fontSize: '1.5rem', fontWeight: 700 }}>
          {!cardData?.profileImageUrl && ini}
        </Avatar>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>{name}</Typography>
        <TitleCompanyStack
          title={title}
          company={company}
          boxSx={{ mt: 0.25, textAlign: 'center' }}
          titleSx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.35 }}
          companySx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.35 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mt: 1.5, flexWrap: 'wrap' }}>
          {cardData?.phone && (
            <Box sx={{ px: 1, py: 0.3, borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', fontSize: '0.55rem', color: 'white', display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <CallIcon sx={{ fontSize: 10 }} /> Call
            </Box>
          )}
          {(cardData?.whatsapp || cardData?.phone) && (
            <Box sx={{ px: 1, py: 0.3, borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', fontSize: '0.55rem', color: 'white', display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <WhatsAppIcon sx={{ fontSize: 10 }} /> WhatsApp
            </Box>
          )}
          {cardData?.email && (
            <Box sx={{ px: 1, py: 0.3, borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', fontSize: '0.55rem', color: 'white', display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <EmailIcon sx={{ fontSize: 10 }} /> Email
            </Box>
          )}
        </Box>
      </Box>

      {flatOrderedSectionNodes(sections, cardData, socialLinks, accent, 'hero', previewOpts)}

      <SectionWhatsApp cardData={cardData} />
      <SectionGoogleMaps cardData={cardData} />
      <SectionPayments cardData={cardData} />
      <FloatingButtons />
      <PoweredBy />
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// ROWS LAYOUT — accent gradient line, horizontal header, icon rows
// ═══════════════════════════════════════════════════════════════
const RowsLayout = ({ cardData, cfg, sections, socialLinks, customLinks, customSections, gallerySection }) => {
  const accent = cfg.accent || '#6366f1';
  const name = cardData?.name || 'Your Name';
  const title = cardData?.title || '';
  const company = cardData?.company || '';
  const ini = initials(name);
  const previewOpts = { customLinks, customSections, gallerySection };

  return (
    <Box sx={{ bgcolor: 'white', minHeight: '100%' }}>
      {cardData?.bannerImageUrl ? (
        <BannerStrip imageUrl={cardData.bannerImageUrl} />
      ) : (
        <Box sx={{ height: 5, background: cfg.accentGradient || `linear-gradient(90deg, ${accent}, ${accent}88)` }} />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 2 }}>
        <Avatar src={cardData?.profileImageUrl || undefined}
          sx={{ width: 56, height: 56, flexShrink: 0, bgcolor: cfg.avatarBg || '#ede9fe', color: cfg.avatarColor || accent, fontWeight: 700, fontSize: '1.1rem' }}>
          {!cardData?.profileImageUrl && ini}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', lineHeight: 1.2 }}>{name}</Typography>
          <TitleCompanyStack
            title={title}
            company={company}
            boxSx={{ mt: 0.25 }}
            titleSx={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.35 }}
            companySx={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.35 }}
          />
        </Box>
      </Box>

      {flatOrderedSectionNodes(sections, cardData, socialLinks, accent, 'rows', previewOpts)}

      <SectionWhatsApp cardData={cardData} />
      <SectionGoogleMaps cardData={cardData} />
      <SectionPayments cardData={cardData} />
      <FloatingButtons />
      <PoweredBy />
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// DARK LAYOUT — elegant dark card with centered profile & glass sections
// ═══════════════════════════════════════════════════════════════
const DarkLayout = ({ cardData, cfg, sections, socialLinks, customLinks, customSections, gallerySection }) => {
  const accent = cfg.accent || '#00A7E3';
  const textPrimary = cfg.textPrimary || '#ffffff';
  const textSecondary = cfg.textSecondary || '#94a3b8';
  const textMuted = cfg.textMuted || '#64748b';
  const cardBg = cfg.cardBg || '#1e293b';
  const surfaceBg = `${textMuted}18`;
  const name = cardData?.name || 'Your Name';
  const title = cardData?.title || '';
  const company = cardData?.company || '';
  const ini = initials(name);
  const previewOpts = { customLinks, customSections, gallerySection };

  return (
    <Box sx={{ bgcolor: cardBg, minHeight: '100%' }}>
      {cardData?.bannerImageUrl && <BannerStrip imageUrl={cardData.bannerImageUrl} fallbackBg={cardBg} />}

      {/* Profile header — centered */}
      <Box sx={{ textAlign: 'center', pt: 2.5, pb: 2, px: 2 }}>
        <Avatar src={cardData?.profileImageUrl || undefined}
          sx={{ width: 64, height: 64, mx: 'auto', mb: 1.25, border: `2.5px solid ${accent}55`, bgcolor: `${accent}30`, color: accent, fontSize: '1.3rem', fontWeight: 700, boxShadow: `0 0 20px ${accent}20` }}>
          {!cardData?.profileImageUrl && ini}
        </Avatar>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: textPrimary, lineHeight: 1.2, letterSpacing: '-0.01em' }}>{name}</Typography>
        <TitleCompanyStack
          title={title}
          company={company}
          boxSx={{ mt: 0.4, textAlign: 'center' }}
          titleSx={{ fontSize: '0.6rem', color: accent, fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.35 }}
          companySx={{ fontSize: '0.6rem', color: textSecondary, fontWeight: 500, letterSpacing: '0.03em', lineHeight: 1.35 }}
        />
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {flatDarkOrderedSectionNodes(sections, cardData, socialLinks, accent, surfaceBg, textPrimary, textSecondary, textMuted, previewOpts)}
      </Box>

      <SectionWhatsApp cardData={cardData} />
      <SectionGoogleMaps cardData={cardData} />
      <SectionPayments cardData={cardData} titleColor={textPrimary} />
      <FloatingButtons />
      <PoweredBy color={textMuted} />
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// SPLIT LAYOUT — colored sidebar, content right
// ═══════════════════════════════════════════════════════════════
const SplitLayout = ({ cardData, cfg, sections, socialLinks, customLinks, customSections, gallerySection }) => {
  const accent = cfg.accent || '#0d9488';
  const name = cardData?.name || 'Your Name';
  const title = cardData?.title || '';
  const company = cardData?.company || '';
  const ini = initials(name);
  const previewOpts = { customLinks, customSections, gallerySection };

  return (
    <Box sx={{ display: 'flex', minHeight: '100%' }}>
      <Box sx={{ width: 70, background: cfg.sidebarBg || `linear-gradient(180deg, ${accent}, ${accent}dd)`, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, flexShrink: 0 }}>
        <Avatar src={cardData?.profileImageUrl || undefined}
          sx={{ width: 40, height: 40, mb: 1, bgcolor: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)', color: 'white', fontWeight: 700, fontSize: '0.8rem' }}>
          {!cardData?.profileImageUrl && ini}
        </Avatar>
      </Box>

      <Box sx={{ flex: 1, bgcolor: 'white', display: 'flex', flexDirection: 'column' }}>
        {cardData?.bannerImageUrl && <BannerStrip imageUrl={cardData.bannerImageUrl} />}
        <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', lineHeight: 1.2 }}>{name}</Typography>
          {title && <Typography sx={{ fontSize: '0.7rem', color: accent, fontWeight: 600, mt: 0.25 }}>{title}</Typography>}
          {company && <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mt: 0.15 }}>{company}</Typography>}

          {flatOrderedSectionNodes(sections, cardData, socialLinks, accent, 'split', previewOpts)}

          <SectionWhatsApp cardData={cardData} />
          <SectionGoogleMaps cardData={cardData} />
          <SectionPayments cardData={cardData} />
        </Box>
        <PoweredBy />
      </Box>
      <FloatingButtons />
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════
// Main component — picks layout by template config
// ═══════════════════════════════════════════════════════════════
const LAYOUTS = {
  hero: HeroLayout,
  rows: RowsLayout,
  dark: DarkLayout,
  split: SplitLayout,
};

const LivePreviewCard = ({
  cardData,
  templateName,
  sections = [],
  socialLinks = [],
  customLinks = [],
  customSections = [],
  gallerySection = { enabled: true, layout: 'grid6', images: [] },
}) => {
  const cfg = getConfig(templateName);
  const Layout = LAYOUTS[cfg.layout] || HeroLayout;
  return (
    <Layout
      cardData={cardData}
      cfg={cfg}
      sections={sections}
      socialLinks={socialLinks}
      customLinks={customLinks}
      customSections={customSections}
      gallerySection={gallerySection}
    />
  );
};

export default LivePreviewCard;
